import { useState, useRef, useEffect, useCallback } from "react";
import type { Scenario, ScenarioPreview } from "../types/scenario";
import { cn } from "../utils/cn";
import {
  formatCurrency,
  displayPercentage,
  displayAge,
  formatYAxisValue,
} from "../utils/formatting";
import { suggestScenarioName } from "../utils/scenarioNameSuggestion";

export interface ScenarioSelectorProps {
  /** Currently displayed scenario name (null = "Current Scenario") */
  activeScenarioName: string | null;
  /** Whether current settings match a saved scenario */
  isCurrentSettingsSaved: boolean;
  /** List of saved scenarios */
  scenarios: Scenario[];
  /** Loading state */
  isLoading?: boolean;
  /** Error message */
  error?: string;
  /** Preview metrics for save modal */
  preview: ScenarioPreview;
  /** Whether a save operation is in progress */
  isSaving?: boolean;
  /** Called when user selects a scenario to load */
  onLoad: (scenario: Scenario) => Promise<void> | void;
  /** Called when user deletes a scenario */
  onDelete: (scenarioId: string) => Promise<void> | void;
  /** Called to save the current settings as a scenario */
  onSave: (input: { name: string; description: string }) => Promise<void>;
  /** Called when user wants to compare selected scenarios */
  onCompare?: (scenarioIds: string[]) => void;
  /** Maximum number of scenarios allowed */
  maxScenarios?: number;
  /** Disable the selector (e.g. during What-If scenario mode) */
  disabled?: boolean;
  /** Tooltip text shown when the selector is disabled */
  disabledTooltip?: string;
}

type DropdownView = "list" | "save";

/**
 * ScenarioSelector — unified dropdown combining scenario display, load, and save.
 *
 * The button always shows the active scenario name (or "Current Scenario").
 * The dropdown panel shows saved scenarios with preview metrics and a save option.
 * Design mirrors the mockup from MVP_2_WEEK_CHECKLIST.md.
 */
export const ScenarioSelector = ({
  activeScenarioName,
  isCurrentSettingsSaved,
  scenarios,
  isLoading,
  error,
  preview,
  isSaving = false,
  maxScenarios = 5,
  onLoad,
  onDelete,
  onSave,
  onCompare,
  disabled = false,
  disabledTooltip,
}: ScenarioSelectorProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [view, setView] = useState<DropdownView>("list");
  const [saveName, setSaveName] = useState("");
  const [saveDescription, setSaveDescription] = useState("");
  const [saveError, setSaveError] = useState<string | undefined>(undefined);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  const [isMultiSelect, setIsMultiSelect] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const containerRef = useRef<HTMLDivElement>(null);

  const displayName = activeScenarioName
    ? `Scenario: ${activeScenarioName}`
    : "Scenario: Current Scenario";
  const saveErrorId = saveError ? "scenario-save-error" : undefined;

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
        setView("list");
        setIsMultiSelect(false);
        setSelectedIds(new Set());
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsOpen(false);
        setView("list");
        setIsMultiSelect(false);
        setSelectedIds(new Set());
      }
    };
    document.addEventListener("keydown", handleEscape);
    return () => document.removeEventListener("keydown", handleEscape);
  }, [isOpen]);

  const handleLoad = async (scenario: Scenario) => {
    await onLoad(scenario);
    setIsOpen(false);
    setView("list");
  };

  const handleDelete = async (scenarioId: string) => {
    if (confirmDeleteId === scenarioId) {
      await onDelete(scenarioId);
      // Only clear if no other scenario's confirm was requested during async delete
      setConfirmDeleteId((prev) => (prev === scenarioId ? null : prev));
    } else {
      setConfirmDeleteId(scenarioId);
    }
  };

  const handleSave = useCallback(async () => {
    if (!saveName.trim()) {
      setSaveError("Scenario name is required");
      return;
    }
    if (saveName.trim().length > 50) {
      setSaveError("Scenario name must be 50 characters or less");
      return;
    }
    if (saveDescription.trim().length > 200) {
      setSaveError("Description must be 200 characters or less");
      return;
    }
    try {
      setSaveError(undefined);
      await onSave({
        name: saveName.trim(),
        description: saveDescription.trim(),
      });
      setSaveName("");
      setSaveDescription("");
      setView("list");
      setIsOpen(false);
    } catch (err) {
      setSaveError(
        err instanceof Error ? err.message : "Failed to save scenario"
      );
    }
  }, [saveName, saveDescription, onSave]);

  const toggleOpen = () => {
    if (!isOpen) {
      setView("list");
      setSaveError(undefined);
      setConfirmDeleteId(null);
      setIsMultiSelect(false);
      setSelectedIds(new Set());
    }
    setIsOpen(!isOpen);
  };

  const enterMultiSelect = () => {
    setIsMultiSelect(true);
    setSelectedIds(new Set());
  };

  const exitMultiSelect = () => {
    setIsMultiSelect(false);
    setSelectedIds(new Set());
  };

  const toggleSelection = (scenarioId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(scenarioId)) {
        next.delete(scenarioId);
      } else {
        next.add(scenarioId);
      }
      return next;
    });
  };

  const handleCompareSelected = () => {
    if (selectedIds.size >= 2 && onCompare) {
      onCompare(Array.from(selectedIds));
      setIsOpen(false);
      setIsMultiSelect(false);
      setSelectedIds(new Set());
    }
  };

  const sortedScenarios = [...scenarios].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  const suggestedName = suggestScenarioName(
    preview,
    scenarios.map((s) => s.name)
  );

  const previewScoreClass =
    preview.readinessScore >= 75
      ? "scenario-preview-score--good"
      : preview.readinessScore >= 50
        ? "scenario-preview-score--fair"
        : "scenario-preview-score--poor";

  return (
    <div
      ref={containerRef}
      className="scenario-selector-container"
      id="scenario-selector"
    >
      {/* Trigger button — shows active scenario name with chevron */}
      <button
        onClick={disabled ? undefined : toggleOpen}
        className="scenario-trigger"
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-disabled={disabled || undefined}
        disabled={disabled}
        title={disabled ? disabledTooltip : displayName}
        aria-description={
          !isCurrentSettingsSaved ? "Unsaved changes" : undefined
        }
      >
        {/* Scenario layers icon */}
        <svg
          className="scenario-trigger-icon"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
          />
        </svg>

        <div className="scenario-trigger-content">
          <div className="scenario-trigger-header">
            <span className="scenario-name-display">{displayName}</span>
            {!isCurrentSettingsSaved && (
              <>
                <span
                  className="scenario-unsaved-indicator"
                  aria-hidden="true"
                />
                <span className="scenario-unsaved-label">Unsaved changes</span>
              </>
            )}
          </div>
          {/* Inline preview metrics */}
          <div className="scenario-preview">
            <span className="scenario-preview-metric">
              <span aria-hidden="true">🎂</span>
              <span className="scenario-preview-label">Retire</span>
              <span className="scenario-preview-value">
                {displayAge(preview.retirementAge)}
              </span>
            </span>
            <span className="scenario-preview-separator" aria-hidden="true">
              ·
            </span>
            <span className="scenario-preview-metric">
              <span aria-hidden="true">💰</span>
              <span className="scenario-preview-label">Worth</span>
              <span className="scenario-preview-value scenario-preview-value--primary">
                {formatYAxisValue(preview.finalValue, preview.currency)}
              </span>
            </span>
            <span className="scenario-preview-separator" aria-hidden="true">
              ·
            </span>
            <span className="scenario-preview-metric">
              <span aria-hidden="true">📊</span>
              <span className="scenario-preview-label">Score</span>
              <span className={cn("scenario-preview-value", previewScoreClass)}>
                {displayPercentage(preview.readinessScore / 100)}
              </span>
            </span>
          </div>
        </div>
        <svg
          className={cn("scenario-chevron", isOpen && "scenario-chevron-open")}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {/* Dropdown panel */}
      {isOpen && (
        <div className="scenario-panel" role="menu">
          {view === "list" ? (
            <>
              {/* Header */}
              <div className="scenario-panel-header">
                <div className="scenario-header-row">
                  <h3 className="scenario-list-heading">Saved Scenarios</h3>
                  <span className="scenario-count">
                    {scenarios.length}/{maxScenarios} saved
                  </span>
                </div>
              </div>

              {/* Multi-select banner */}
              {isMultiSelect && (
                <div className="scenario-multiselect-banner">
                  ⇄ Select scenarios to compare (2–3)
                </div>
              )}

              {/* Scenario list */}
              {isLoading ? (
                <div className="scenario-empty-state">Loading scenarios…</div>
              ) : error ? (
                <div className="scenario-error-message">{error}</div>
              ) : scenarios.length === 0 ? (
                <div className="scenario-empty-state" id="scenario-empty-state">
                  <p className="scenario-empty-state-title">
                    📸 No scenarios yet
                  </p>
                  <p className="scenario-empty-state-description">
                    Save a snapshot of your current plan to compare different
                    retirement strategies.
                  </p>
                  <button
                    onClick={() => {
                      setView("save");
                      setSaveName("");
                      setSaveDescription("");
                      setSaveError(undefined);
                    }}
                    className="scenario-empty-state-cta"
                    disabled={isCurrentSettingsSaved}
                  >
                    + Save current plan as scenario
                  </button>
                </div>
              ) : (
                <ul className="scenario-list-container">
                  {sortedScenarios.map((scenario) => {
                    const isActive =
                      activeScenarioName === scenario.name &&
                      isCurrentSettingsSaved;
                    const previewData =
                      scenario.preview ?? scenario.projections;
                    const retirementAgeText = previewData
                      ? displayAge(previewData.retirementAge)
                      : "--";
                    const finalValueText = previewData
                      ? formatCurrency(previewData.finalValue, "CAD")
                      : "--";
                    const scoreText = previewData
                      ? displayPercentage(previewData.readinessScore / 100)
                      : "--";
                    const scoreColor = previewData
                      ? previewData.readinessScore >= 90
                        ? "scenario-score-excellent"
                        : previewData.readinessScore >= 70
                          ? "scenario-score-warning"
                          : "scenario-score-danger"
                      : "scenario-score-neutral";

                    const isSelected = selectedIds.has(scenario.id);
                    const isSelectionFull =
                      selectedIds.size >= 3 && !isSelected;

                    return (
                      <li key={scenario.id}>
                        <div
                          className={cn(
                            "scenario-row",
                            isActive
                              ? "scenario-row-active"
                              : "scenario-row-default"
                          )}
                        >
                          {/* Multi-select checkbox */}
                          {isMultiSelect && (
                            <input
                              type="checkbox"
                              className="scenario-checkbox"
                              checked={isSelected}
                              disabled={isSelectionFull}
                              onChange={() => toggleSelection(scenario.id)}
                              aria-label={`Select ${scenario.name} for comparison`}
                            />
                          )}
                          <button
                            onClick={
                              isMultiSelect
                                ? () =>
                                    !isSelectionFull &&
                                    toggleSelection(scenario.id)
                                : () => handleLoad(scenario)
                            }
                            className="scenario-button-content"
                            role="menuitem"
                          >
                            <div className="scenario-name-row">
                              <p
                                className="scenario-name-text"
                                title={scenario.name}
                              >
                                {scenario.name}
                              </p>
                              {isActive && (
                                <span className="scenario-active-badge">
                                  ACTIVE
                                </span>
                              )}
                            </div>
                            {scenario.description && (
                              <p className="scenario-description-text">
                                {scenario.description}
                              </p>
                            )}
                            <div className="scenario-metrics-row">
                              <span>
                                Age:{" "}
                                <span className="scenario-metric-value">
                                  {retirementAgeText}
                                </span>
                              </span>
                              <span>
                                Value:{" "}
                                <span className="scenario-metric-value-positive">
                                  {finalValueText}
                                </span>
                              </span>
                              <span>
                                Score:{" "}
                                <span
                                  className={cn(
                                    "scenario-score-text",
                                    scoreColor
                                  )}
                                >
                                  {scoreText}
                                </span>
                              </span>
                            </div>
                          </button>
                          {/* Delete — hidden during multi-select */}
                          {!isMultiSelect && (
                            <div className="scenario-actions-container">
                              {confirmDeleteId === scenario.id ? (
                                <div className="scenario-confirm-buttons">
                                  <button
                                    onClick={() => setConfirmDeleteId(null)}
                                    className="scenario-cancel-btn"
                                  >
                                    Cancel
                                  </button>
                                  <button
                                    onClick={() => handleDelete(scenario.id)}
                                    className="scenario-confirm-delete-btn"
                                  >
                                    Confirm
                                  </button>
                                </div>
                              ) : (
                                <button
                                  onClick={() => handleDelete(scenario.id)}
                                  className="scenario-delete-btn"
                                  aria-label={`Delete ${scenario.name}`}
                                >
                                  <svg
                                    className="scenario-icon-small"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                  </svg>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {/* Footer — Save + Compare actions */}
              <div className="scenario-panel-footer">
                {isMultiSelect ? (
                  <div className="scenario-multiselect-footer">
                    <span className="scenario-selection-counter">
                      {selectedIds.size} selected
                    </span>
                    <div className="scenario-multiselect-actions">
                      <button
                        onClick={handleCompareSelected}
                        disabled={selectedIds.size < 2}
                        className="scenario-compare-btn"
                      >
                        Compare Selected
                      </button>
                      <button
                        onClick={exitMultiSelect}
                        className="scenario-cancel-multiselect-btn"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="scenario-footer-actions">
                    <button
                      onClick={() => {
                        setView("save");
                        setSaveName("");
                        setSaveDescription("");
                        setSaveError(undefined);
                      }}
                      disabled={
                        isCurrentSettingsSaved ||
                        scenarios.length >= maxScenarios
                      }
                      className={cn(
                        "scenario-save-btn",
                        isCurrentSettingsSaved ||
                          scenarios.length >= maxScenarios
                          ? "scenario-save-btn-disabled"
                          : "scenario-save-btn-enabled"
                      )}
                    >
                      {isCurrentSettingsSaved
                        ? "View Saved Settings"
                        : "Save as New Scenario"}
                    </button>
                    {scenarios.length >= 2 && onCompare && (
                      <button
                        onClick={enterMultiSelect}
                        className="scenario-compare-trigger-btn"
                      >
                        ⇄ Compare
                      </button>
                    )}
                  </div>
                )}
              </div>
            </>
          ) : (
            /* Save form view */
            <div className="save-scenario-form" id="save-scenario-form">
              {/* Header */}
              <div className="scenario-panel-header-row">
                <h3 className="scenario-panel-subtitle">Save Scenario</h3>
                <button
                  onClick={() => setView("list")}
                  className="scenario-panel-link"
                >
                  ← Back
                </button>
              </div>

              {/* Preview metrics */}
              <div className="scenario-summary-section">
                <div className="summary-grid-2col">
                  <div>
                    <p className="summary-label">Retirement age</p>
                    <p className="summary-value">
                      {displayAge(preview.retirementAge)}
                    </p>
                  </div>
                  <div>
                    <p className="summary-label">Net worth</p>
                    <p className="summary-value">
                      {formatCurrency(preview.finalValue, preview.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="summary-label">Monthly income</p>
                    <p className="summary-value">
                      {formatCurrency(preview.monthlyIncome, preview.currency)}
                    </p>
                  </div>
                  <div>
                    <p className="summary-label">Readiness</p>
                    <p className="summary-value">
                      {displayPercentage(preview.readinessScore / 100)}
                    </p>
                  </div>
                </div>
              </div>

              {/* Form */}
              <div className="scenario-form-container">
                <div>
                  <label
                    htmlFor="scenario-name"
                    className="scenario-form-label"
                  >
                    Name
                  </label>
                  <input
                    id="scenario-name"
                    type="text"
                    value={saveName}
                    onChange={(e) => setSaveName(e.target.value)}
                    placeholder={suggestedName}
                    className="scenario-form-input"
                    disabled={isSaving}
                    aria-invalid={saveError ? "true" : "false"}
                    aria-describedby={saveErrorId}
                  />
                  {saveError && (
                    <p className="form-error" id={saveErrorId} role="alert">
                      {saveError}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="scenario-description"
                    className="scenario-form-label"
                  >
                    Description (optional)
                  </label>
                  <textarea
                    id="scenario-description"
                    value={saveDescription}
                    onChange={(e) => setSaveDescription(e.target.value)}
                    placeholder="Brief notes about this scenario…"
                    rows={2}
                    className="scenario-form-input"
                    disabled={isSaving}
                  />
                </div>

                <div className="scenario-form-actions">
                  <button
                    onClick={() => setView("list")}
                    disabled={isSaving}
                    className="scenario-form-cancel-btn"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="scenario-form-submit-btn"
                  >
                    {isSaving ? "Saving…" : "Save"}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
